import { RowDoubleClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import axios, { AxiosRequestConfig } from 'axios';
import React from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { useQuery } from 'react-query';
import FieldCoreProcessType from '../../../../../domain/fieldCoreProcess';
import PermissionType from '../../../../../domain/permission';
import TaskGridSummary from '../../../../tasks/TaskGridSummary/TaskGridSummary';
import { doActionsContain, hasPermission } from '../../../../../helpers/PermissionHelper';
import { CORE_TRIGGER_RULE_URL, CORE_TRIGGER_RULE_PERMISSIONS_URL } from '../../../../../constants/urls';
import TaskGridTags from '../../../../tasks/TaskGridTags/TaskGridTags';
import {
  getDefaultColDef,
  defaultOnGridReady,
  getDefaultUseQueryConfig,
  useGridComponentStates,
  getDefaultStatusBar,
} from '../../../../../helpers/GridComponentHelpers';
import GenericDeleteDialog from '../../../../GenericDeleteDialog/GenericDeleteDialog';
import { createFeedbackMessageKey, FeedbackMessage } from '../../../../FeedbackMessages/FeedbackMessages';
import GenericGridActions from '../../../../GenericGridActions/GenericGridActions';
import { HQ_TIMEZONE } from '../../../../../constants/globals';
import CoreTriggerRuleType from '../../../../../domain/coreTriggerRule';
import ModifyCoreTriggerRuleDialog, {
  defaultHandleModifyCoreTriggerRuleDialogOk,
} from '../ModifyCoreTriggerRuleDialog/ModifyCoreTriggerRuleDialog';

type CoreTriggerRuleGridProps = {
  fieldCoreProcess: FieldCoreProcessType;
  idToFocusOn?: number;
};

const getColumns = (openModifyDialog: (objectToModify: CoreTriggerRuleType) => void, hqTimezone: string) => {
  return [
    {
      field: 'createOnEventType.name',
      headerName: 'Event',
      width: 200,
    },
    {
      field: 'summaryTemplate',
      headerName: 'Summary',
      // Use flex instead of width to fill up the rest of the space
      // width: null,
      flex: 1,
      cellRenderer: 'summaryRenderer',
    },
    {
      field: 'tags',
      headerName: 'Tags',
      cellRenderer: 'tagsRenderer',
      width: 120,

      // TODO figure out how to do this right
      filter: false,
      // filterParams: {
      //   valueFormatter: (params: ValueFormatterParams) => {
      //     return params.value.map((tag: TagType) => {
      //       return tag.name;
      //     }).join(' ,')
      //   }
      // }
    },
    {
      field: 'suggestedStaff',
      headerName: 'Suggested Staff',
      width: 150,
    },
    {
      field: 'totalPoints',
      headerName: 'Weight',
      width: 90,
    },
    {
      field: 'onNcr',
      headerName: 'NCR?',
      width: 50,
      cellRenderer: (params: any) => {
        return params.value ? <span>✅</span> : <span></span>;
      }
    },
    {
      field: 'onSmr',
      headerName: 'SMR?',
      width: 50,
      cellRenderer: (params: any) => {
        return params.value ? <span>✅</span> : <span></span>;
      }
    },
    {
      headerName: 'Actions',
      width: 90,
      cellRenderer: 'actionsRenderer',
      cellRendererParams: {
        onEditClick: (ruleToEdit: CoreTriggerRuleType) => {
          openModifyDialog(ruleToEdit);
        },
      },
    },
  ];
};

const CoreTriggerRuleGrid = (props: CoreTriggerRuleGridProps) => {
  let gridComponentStates = useGridComponentStates<CoreTriggerRuleType>();
  const [columnDefs] = React.useState(getColumns(gridComponentStates.openModifyDialog, HQ_TIMEZONE));

  const coreTriggerRulesRequestConfig = {
    params: {
      field_core_process: props.fieldCoreProcess.id,
    },
  } as AxiosRequestConfig;

  const {
    isRefetching: isRefetchingRules,
    error: rulesError,
    data: rulesData,
    refetch: refetchRules,
  } = useQuery(
    ['getCoreTriggerRulesForFcp', coreTriggerRulesRequestConfig],
    () => {
      // If the grid isn't ready, this won't work,
      //  but ag grid defaults to showing loading overlay initially anyway
      if (gridComponentStates.gridApi) {
        gridComponentStates.gridApi.showLoadingOverlay();
      }
      return axios.get<CoreTriggerRuleType[]>(CORE_TRIGGER_RULE_URL, coreTriggerRulesRequestConfig);
    },
    getDefaultUseQueryConfig<CoreTriggerRuleType>(
      gridComponentStates.gridApi,
      gridComponentStates.openModifyDialog,
      props.idToFocusOn,
    ),
  );

  const getCoreTriggerRulePermissionsRequestConfig = {
    params: {
      // No parameters
    },
  } as AxiosRequestConfig;
  const {
    error: rulePermissionsError,
    data: rulePermissionsData,
    isLoading: isPermissionsLoading,
  } = useQuery(['getCoreTriggerRulePermissions', getCoreTriggerRulePermissionsRequestConfig], () => {
    return axios.get<PermissionType[]>(CORE_TRIGGER_RULE_PERMISSIONS_URL, getCoreTriggerRulePermissionsRequestConfig);
  });

  if (rulesError || rulePermissionsError) return <p>Error!</p>;

  const coreTriggerRules = rulesData?.data;
  const rulePermissions = rulePermissionsData?.data;

  if (!isPermissionsLoading && (!rulePermissions || rulePermissions.length === 0)) {
    return <p>No permission for core trigger rules.</p>;
  }

  const handleModifyDialogDeleteIcon = (updatedRule: CoreTriggerRuleType) => {
    gridComponentStates.closeModifyDialog();
    gridComponentStates.openDeleteDialog(updatedRule);
  };

  const handleRowDoubleClick = (event: RowDoubleClickedEvent) => {
    const rule = event.data as CoreTriggerRuleType;
    if (doActionsContain(rule, 'change')) {
      gridComponentStates.openModifyDialog(rule);
    }
  };

  const onCreateClick = () => {
    // Null for new object
    gridComponentStates.openModifyDialog(null);
  };

  return (
    <Container
      fluid
      className={
        //styles.container +
        ' d-flex flex-column flex-grow-1'
      }
    >
      <Row className="d-flex flex-column flex-grow-1">
        <Col>
          <div
            className="ag-theme-balham"
            style={{
              height: '200px',
              width: '100%',
            }}
          >
            <AgGridReact
              onGridReady={(params) => {
                defaultOnGridReady(params, coreTriggerRules, isRefetchingRules, gridComponentStates.setGridApi);
              }}
              frameworkComponents={{
                actionsRenderer: GenericGridActions,
                tagsRenderer: TaskGridTags,
                // dateAndCreationIconRenderer: TaskGridDateAndCreationIcon,
                summaryRenderer: TaskGridSummary,
              }}
              onRowDoubleClicked={handleRowDoubleClick}
              columnDefs={columnDefs}
              // 2022-18-07 Kirk, Austin, Brad - filter is too buried, make the button always visible
              suppressMenuHide={true}
              tooltipShowDelay={450}
              defaultColDef={getDefaultColDef()}
              // Normally we pass in row data via api on react-query onSuccess instead.
              // Otherwise this comes through as undefined on user tab switching which breaks loading spinner
              // However, apparently onSuccess doesn't get called when the data was cached
              // That means when you flip Legal to Station Expansion and back to Legal, Legal Cases don't show up
              // Using rowData fixes this, but I'm hoping for a better solution later.
              rowData={coreTriggerRules}
              statusBar={getDefaultStatusBar()}
            ></AgGridReact>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          {rulePermissions && hasPermission(rulePermissions, 'add_coretriggerrule') && (
            <Button variant="primary" onClick={onCreateClick}>
              Create a core trigger rule <i className="bi bi-plus" />
            </Button>
          )}
        </Col>
      </Row>
      <GenericDeleteDialog
        state={gridComponentStates.deleteDialogState}
        onCancel={gridComponentStates.closeDeleteDialog}
        onOk={(idToDelete) =>
          handleDeleteCoreTriggerRuleDialogOk(
            idToDelete,
            gridComponentStates.closeDeleteDialog,
            gridComponentStates.disableSubmissionDeleteDialog,
            refetchRules,
            gridComponentStates.addFeedbackMessage,
          )
        }
        objectTypeString="core trigger rule"
      />
      <ModifyCoreTriggerRuleDialog
        // Should I change how I pass this state somehow?
        state={gridComponentStates.modifyDialogState}
        onCancel={gridComponentStates.closeModifyDialog}
        onOk={(modifyRule, idToUpdate) =>
          defaultHandleModifyCoreTriggerRuleDialogOk(
            modifyRule,
            idToUpdate,
            gridComponentStates.closeModifyDialog,
            refetchRules,
            gridComponentStates.addFeedbackMessage,
          )
        }
        onDeleteIcon={handleModifyDialogDeleteIcon}
        fieldCoreProcess={props.fieldCoreProcess}
      />
    </Container>
  );
};

// TODO should I make this generic, too?
export function handleDeleteCoreTriggerRuleDialogOk(
  idToDelete: number,
  closeDeleteDialog: () => void,
  disableSubmissionDeleteDialog: () => void,
  refetch: () => void,
  addFeedbackMessage: (feedbackMessage: FeedbackMessage) => void,
) {
  disableSubmissionDeleteDialog();
  return axios.delete(CORE_TRIGGER_RULE_URL + '/' + idToDelete + '/').then((response) => {
    closeDeleteDialog();
    addFeedbackMessage({
      key: createFeedbackMessageKey('triggerRule', 'delete', idToDelete),
      status: 'success',
      messageBody: <span>Trigger rule deleted successfully.</span>,
    });
    refetch();
  });
}

export default CoreTriggerRuleGrid;
